// FoliateReaderView — PageVoice 1.0 reader on foliate-js.
//
// Built to replace the epub.js ReaderView, which can't repaint/turn pages on
// iOS Safari during read-aloud. This engine drives the page FROM the audio
// position (view.goTo per paragraph), which paginates correctly on Safari.
//
// Milestone 1: opens a real library book, reads it aloud (Kokoro), word +
// paragraph highlight, pause/resume, start-from-current-page, position resume.
// Later: translation, notes/highlights persistence, settings panel, then this
// replaces ReaderView entirely.
import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getBook, getBookBlob } from "@/services/bookStorageService";
import { remoteUrl } from "@/services/remoteConfig";
import { ReaderToolbar, PlayPauseGlyph } from "./ReaderToolbar";
import { useReaderStore } from "@/store/readerStore";
import { SettingsPanel } from "./SettingsPanel";
import { TOCPanel } from "./TOCPanel";
import { TranslatePanel, fetchTranslation } from "./TranslatePanel";
import { loadPretranslationForBook, paraEnglish } from "@/services/pretranslateService";
import { addBookmark, deleteBookmark, listBookmarks } from "@/services/notesService";
import type { ReaderSettings } from "@/types/reader";

// Target-language name → BCP-47 code, for picking a browser voice when
// reading the TRANSLATED text aloud (so it's pronounced in that language).
const LANG_CODE: Record<string, string> = {
  English: "en-US", French: "fr-FR", Spanish: "es-ES", German: "de-DE",
  Italian: "it-IT", Portuguese: "pt-PT", Dutch: "nl-NL", Russian: "ru-RU",
  Japanese: "ja-JP", Chinese: "zh-CN", Korean: "ko-KR", Arabic: "ar-SA",
  Hindi: "hi-IN", Polish: "pl-PL", Turkish: "tr-TR", Swedish: "sv-SE",
  Norwegian: "nb-NO", Danish: "da-DK", Finnish: "fi-FI", Greek: "el-GR",
  Czech: "cs-CZ", Ukrainian: "uk-UA", Romanian: "ro-RO", Hungarian: "hu-HU",
};

// Reading-area CSS built from the user's reader settings (theme/font), applied
// to the foliate content via view.renderer.setStyles — same mechanism the old
// epub reader uses (applyReaderTheme), so Settings changes actually take effect.
const THEME_BG: Record<string, { bg: string; fg: string; link: string }> = {
  paper: { bg: "#fdfcf8", fg: "#1b1712", link: "#5a4a2a" },
  white: { bg: "#ffffff", fg: "#1a1a1a", link: "#3b3b3b" },
  sepia: { bg: "#f4ecd8", fg: "#5b4636", link: "#704214" },
  gray: { bg: "#cfd2cd", fg: "#1a1a1a", link: "#222222" },
  dark: { bg: "#0e0e10", fg: "#e8e6e1", link: "#cfcfcf" },
  "eink-light": { bg: "#ffffff", fg: "#000000", link: "#000000" },
  "eink-dark": { bg: "#000000", fg: "#ffffff", link: "#ffffff" },
};
function themeBg(theme: string) { return (THEME_BG[theme] || THEME_BG.paper).bg; }
function readerCss(r: ReaderSettings): string {
  const p = THEME_BG[r.theme] || THEME_BG.paper;
  const align = r.theme === "paper" ? "justify" : "left";
  const scheme = r.theme === "dark" || r.theme === "eink-dark" ? "only dark" : "only light";
  return `
    @namespace epub "http://www.idpf.org/2007/ops";
    html { color-scheme: ${scheme}; }
    html, body { background: ${p.bg} !important; color: ${p.fg} !important;
      font-family: ${r.fontFamily} !important; font-size: ${r.fontSize}px !important;
      line-height: ${r.lineHeight} !important; }
    p,li,blockquote,dd,h1,h2,h3,h4,h5,h6,span,em,strong { color: ${p.fg} !important; }
    p,li,blockquote,dd { line-height: ${r.lineHeight} !important; text-align: ${align};
      hyphens: auto; -webkit-hyphens: auto; }
    a { color: ${p.link} !important; }
    .pv-reading { background: rgba(240,185,70,.22); border-radius:3px; }
    .pv-w-on { background: rgba(240,185,70,.85); border-radius:2px;
      box-shadow: 0 0 0 1px rgba(200,140,20,.4); }
  `;
}

const TTS = remoteUrl("/tts-kokoro/tts");
const VOICE = "af_bella";
const SILENT =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";
const READ_CSS = `
  @namespace epub "http://www.idpf.org/2007/ops";
  html { color-scheme: only light; font-size: 150%; }
  html, body { background: transparent !important; color: #23201a !important; }
  p,li,blockquote,dd,h1,h2,h3,h4,h5,h6,span,em,strong,a { color: #23201a !important; }
  a { color: #1a4fb0 !important; }
  p,li,blockquote,dd { line-height:1.6; }
  .pv-reading { background: rgba(240,185,70,.22); border-radius:3px; }
  .pv-w-on { background: rgba(240,185,70,.85); border-radius:2px;
    box-shadow: 0 0 0 1px rgba(200,140,20,.4); }
`;

type PlayState = "idle" | "playing" | "paused";

export function FoliateReaderView() {
  const { bookId = "" } = useParams();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState("loading…");
  const [title, setTitle] = useState("");
  const [fatal, setFatal] = useState<string | null>(null);
  const [playState, setPlayState] = useState<PlayState>("idle");
  const [frac, setFrac] = useState(0);            // reading progress 0..1 (bottom bar)
  const [chapter, setChapter] = useState("");
  const [chromeVisible, setChromeVisible] = useState(false);  // immersive by default; tap top/bottom to reveal menus
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [toc, setToc] = useState<any[]>([]);
  const [bmId, setBmId] = useState<string | null>(null);
  const reader = useReaderStore((s) => s.reader);
  const setReader = useReaderStore((s) => s.setReader);
  const rate = useReaderStore((s) => s.tts.rate);
  const [translatePage, setTranslatePage] = useState<{ text: string; paras?: string[]; cfi: string | undefined }>({ text: "", cfi: undefined });
  // getVisiblePageText() lives in the effect (needs the engine); exposed here.
  const gvpRef = useRef<() => { text: string; paras?: string[]; cfi: string | undefined }>(() => ({ text: "", cfi: undefined }));
  const barRef = useRef<HTMLDivElement | null>(null);
  const [barH, setBarH] = useState(96);   // bottom-bar height, reserved under the translate overlay
  const [txIdx, setTxIdx] = useState<number | null>(null);   // char index spoken in the translated overlay
  const txRef = useRef(translatePage); txRef.current = translatePage;
  // Live refs so the running engine (in the effect closure) can read the
  // latest settings + position without re-subscribing.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const viewRef = useRef<any>(null);
  const rateRef = useRef(rate); rateRef.current = rate;
  const readerRef = useRef(reader); readerRef.current = reader;
  const curCfiRef = useRef<string | null>(null);
  const curHrefRef = useRef<string | undefined>(undefined);
  const bmRef = useRef<{ id: string; cfi: string }[]>([]);
  // Apply/restore in-place translation for the current section (set by effect).
  const txApplyRef = useRef<(on: boolean) => void>(() => {});
  // Imperative engine controls, filled in by the effect.
  const ctl = useRef<{ toggle: () => void; prev: () => void; next: () => void; stop: () => void }>({
    toggle: () => {}, prev: () => {}, next: () => {}, stop: () => {},
  });

  // Apply reader theme/font changes to the live foliate content.
  useEffect(() => {
    try { viewRef.current?.renderer?.setStyles?.(readerCss(reader)); } catch { /* */ }
  }, [reader]);

  // Toggling translation stops playback (the read loop differs per mode) and,
  // when turning on, snapshots the current visible page immediately.
  useEffect(() => {
    try { ctl.current.stop?.(); } catch { /* */ }
    // Translate the book IN PLACE (swap paragraph text) so foliate paginates
    // the English natively — real pages, working edges/swipe, no overlay.
    try { txApplyRef.current(reader.translateOn); } catch { /* */ }
  }, [reader.translateOn]);

  // Measure the bottom bar so the translation overlay reserves room for it.
  // When the chrome is hidden the bar isn't rendered, so reserve nothing.
  useEffect(() => {
    if (!chromeVisible) { setBarH(0); return; }
    const measure = () => { if (barRef.current) setBarH(barRef.current.offsetHeight); };
    measure();
    const t = setTimeout(measure, 30);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [chromeVisible]);

  // Bookmark toggle for the current page.
  async function toggleBookmark() {
    const cfi = curCfiRef.current;
    if (!cfi) return;
    if (bmId) {
      try { await deleteBookmark(bmId); } catch { /* */ }
      bmRef.current = bmRef.current.filter((b) => b.id !== bmId);
      setBmId(null);
    } else {
      try {
        const b = await addBookmark({ bookId, cfi, href: curHrefRef.current, chapterLabel: chapter || undefined });
        bmRef.current = [...bmRef.current, { id: b.id, cfi: b.cfi }];
        setBmId(b.id);
      } catch { /* */ }
    }
  }

  useEffect(() => {
    let disposed = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let view: any = null;
    const audio = new Audio();
    audio.preload = "auto";

    const state = {
      playing: false, paused: false, stopped: false, gen: 0,
      curLoadedIndex: 0, curCfi: null as string | null,
      activeWordSpans: null as HTMLElement[] | null,
    };
    // index -> [{ cfi, text, el }]
    const sectionBlocks: Record<number, { cfi: string | null; text: string; el: Element }[]> = {};
    const audioCache = new Map<string, Promise<ArrayBuffer>>();
    const posKey = `pv.boot.cfi.${bookId}`;
    const log = (m: string) => { if (!disposed) setStatus(m); };
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    function setUI(s: PlayState) { if (!disposed) setPlayState(s); }

    function unlock() {
      state.stopped = false;
      try {
        audio.src = SILENT;
        const p = audio.play();
        if (p && p.then) p.then(() => { try { audio.pause(); audio.currentTime = 0; } catch { /* */ } }).catch(() => {});
      } catch { /* */ }
    }

    function fetchAudio(text: string): Promise<ArrayBuffer> {
      const hit = audioCache.get(text);
      if (hit) return hit;
      const p = fetch(TTS, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, voice: VOICE, rate: rateRef.current || 1 }),
      }).then((r) => { if (!r.ok) throw new Error("tts " + r.status); return r.arrayBuffer(); });
      p.catch(() => audioCache.delete(text));
      audioCache.set(text, p);
      while (audioCache.size > 6) audioCache.delete(audioCache.keys().next().value as string);
      return p;
    }

    async function speak(text: string) {
      const buf = await fetchAudio(text);
      if (state.stopped) return;
      const url = URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
      await new Promise<void>((resolve) => {
        if (state.stopped) { resolve(); return; }
        const spans = state.activeWordSpans;
        const onTime = () => {
          if (!spans || !spans.length || !audio.duration || !isFinite(audio.duration)) return;
          const idx = Math.min(spans.length - 1, Math.floor((audio.currentTime / audio.duration) * spans.length));
          for (let i = 0; i < spans.length; i++) spans[i].classList.toggle("pv-w-on", i === idx);
        };
        const cleanup = () => {
          audio.removeEventListener("ended", onEnd);
          audio.removeEventListener("error", onErr);
          audio.removeEventListener("timeupdate", onTime);
          URL.revokeObjectURL(url);
        };
        const onEnd = () => { cleanup(); resolve(); };
        const onErr = () => { cleanup(); resolve(); };
        audio.addEventListener("ended", onEnd);
        audio.addEventListener("error", onErr);
        if (spans && spans.length) audio.addEventListener("timeupdate", onTime);
        audio.src = url;
        const pp = audio.play();
        if (pp && pp.catch) pp.catch(() => { cleanup(); resolve(); });
      });
    }

    function wrapWords(el: Element): HTMLElement[] {
      const doc = el.ownerDocument;
      const spans: HTMLElement[] = [];
      const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const nodes: Text[] = [];
      let n: Node | null;
      while ((n = walker.nextNode())) nodes.push(n as Text);
      for (const tn of nodes) {
        if (!tn.nodeValue || !tn.nodeValue.trim()) continue;
        const parts = tn.nodeValue.split(/(\s+)/);
        const frag = doc.createDocumentFragment();
        for (const part of parts) {
          if (part === "") continue;
          if (/^\s+$/.test(part)) { frag.appendChild(doc.createTextNode(part)); continue; }
          const s = doc.createElement("span");
          s.className = "pv-w"; s.textContent = part;
          frag.appendChild(s); spans.push(s);
        }
        tn.parentNode?.replaceChild(frag, tn);
      }
      return spans;
    }
    function unwrap(el: Element) {
      el.querySelectorAll("span.pv-w").forEach((s) =>
        s.replaceWith(el.ownerDocument.createTextNode(s.textContent || "")));
      try { (el as HTMLElement).normalize(); } catch { /* */ }
    }
    function clearHighlights() {
      for (const idx in sectionBlocks)
        for (const b of sectionBlocks[idx])
          if (b.el) { b.el.classList.remove("pv-reading"); try { unwrap(b.el); } catch { /* */ } }
    }

    function startBlock(blocks: { cfi: string | null }[], compare: (a: string, b: string) => number) {
      if (!state.curCfi) return 0;
      for (let i = 0; i < blocks.length; i++) {
        if (!blocks[i].cfi) continue;
        try { if (compare(blocks[i].cfi as string, state.curCfi) >= 0) return i; } catch { return 0; }
      }
      return 0;
    }

    // Index of the first block actually on the visible page (by geometry).
    // This is what "start reading from the current page" needs — the CFI
    // compare can miss (returning 0 → the very start of the section), which
    // is why Play felt stuck at the first reading point.
    // Is this block actually on the visible SCREEN page? foliate sizes the
    // content iframe to the whole section (all columns) and scrolls its parent,
    // so a block's rect is section-space; measuring against the iframe's own
    // clientWidth marks EVERY block "visible" (that's why translate grabbed the
    // whole section as one scroll). Map the rect to top-viewport coords via the
    // iframe's on-screen rect, then test against the real screen.
    function onScreen(el: HTMLElement): boolean {
      if (!el || !el.isConnected) return false;
      let r: DOMRect;
      try { r = el.getBoundingClientRect(); } catch { return false; }
      const doc = el.ownerDocument;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const win = doc.defaultView as any;
      let ox = 0, oy = 0, W = 0, H = 0;
      try {
        const fe = win?.frameElement as HTMLElement | null;
        const top = win?.top as Window | null;
        if (fe && top) { const fr = fe.getBoundingClientRect(); ox = fr.left; oy = fr.top; W = top.innerWidth; H = top.innerHeight; }
      } catch { /* cross-origin */ }
      if (!W || !H) { // fallback: iframe-local (best effort)
        W = doc.documentElement?.clientWidth || win?.innerWidth || 0;
        H = doc.documentElement?.clientHeight || win?.innerHeight || 0;
        ox = 0; oy = 0;
      }
      if (!W || !H) return false;
      const l = r.left + ox, rt = r.right + ox, t = r.top + oy, b = r.bottom + oy;
      return rt > 4 && l < W - 4 && b > 4 && t < H - 4;
    }

    function firstVisibleIndex(): number {
      const blocks = sectionBlocks[state.curLoadedIndex] || [];
      for (let i = 0; i < blocks.length; i++) {
        if (onScreen(blocks[i].el as HTMLElement)) return i;
      }
      return -1;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function readLoop(compare: any) {
      const myGen = ++state.gen;
      state.playing = true; state.paused = false; setUI("playing");
      let idx = state.curLoadedIndex;
      let bi = firstVisibleIndex();
      if (bi < 0) bi = startBlock(sectionBlocks[idx] || [], compare);
      if (bi < 0) bi = 0;
      while (state.playing && state.gen === myGen) {
        const blocks = sectionBlocks[idx] || [];
        if (bi >= blocks.length) {
          const before = state.curLoadedIndex;
          log("turning page…");
          try { await view.next(); } catch { /* */ }
          const deadline = Date.now() + 3000;
          while (state.curLoadedIndex === before && Date.now() < deadline && state.playing) await sleep(50);
          if (state.curLoadedIndex === before) { log("end of book"); break; }
          idx = state.curLoadedIndex; bi = 0; continue;
        }
        const b = blocks[bi];
        if (b.cfi) { try { await view.goTo(b.cfi); await sleep(120); } catch { /* */ } }
        // When translation is on the page text is already the target language;
        // read what's actually on screen (el.textContent), not the source b.text.
        const tOn = readerRef.current.translateOn;
        const speakText = tOn ? ((b.el as HTMLElement)?.textContent || b.text) : b.text;
        const nb = blocks[bi + 1];
        if (nb) fetchAudio(tOn ? ((nb.el as HTMLElement)?.textContent || nb.text) : nb.text).catch(() => {});
        let spans: HTMLElement[] = [];
        if (b.el) { b.el.classList.add("pv-reading"); try { spans = wrapWords(b.el); } catch { /* */ } }
        state.activeWordSpans = spans;
        log(`reading · "${speakText.slice(0, 26)}"`);
        try { await speak(speakText); } catch (e) { log("tts error: " + String(e).slice(0, 36)); }
        state.activeWordSpans = null;
        if (b.el) { b.el.classList.remove("pv-reading"); try { unwrap(b.el); } catch { /* */ } }
        if (!state.playing || state.gen !== myGen) break;
        bi++;
      }
      if (state.gen === myGen) { state.playing = false; state.paused = false; setUI("idle"); }
    }

    function stopRead() {
      state.playing = false; state.paused = false; state.stopped = true; state.gen++;
      try { audio.pause(); audio.src = ""; } catch { /* */ }
      try { window.speechSynthesis?.cancel(); } catch { /* */ }
      if (!disposed) setTxIdx(null);
      clearHighlights(); setUI("idle");
    }

    // Text of the blocks currently on the visible page — fed to TranslatePanel.
    // foliate paginates in columns, so a block is "on screen" when its rect
    // intersects the rendered document's viewport. Falls back to the whole
    // section if geometry isn't available.
    function getVisiblePageText(): { text: string; paras?: string[]; cfi: string | undefined } {
      const blocks = sectionBlocks[state.curLoadedIndex] || [];
      const vis: typeof blocks = [];
      for (const b of blocks) {
        if (onScreen(b.el as HTMLElement)) vis.push(b);
      }
      const src = vis.length ? vis : blocks;
      const paras = src.map((b) => b.text).filter(Boolean);
      return { text: paras.join("\n\n"), paras, cfi: (vis[0] || blocks[0])?.cfi || state.curCfi || undefined };
    }
    gvpRef.current = getVisiblePageText;

    // ── In-place translation ──────────────────────────────────────────
    // Swap each paragraph's text for its English so foliate paginates the
    // translation itself (true pages, working edges/swipe/read-aloud) instead
    // of a separate scrolling overlay. Uses the bound pretranslation map first
    // (instant for QL2 and the shipped classics); anything the map misses is
    // filled in progressively by a live /lib/translate call. Original text is
    // stashed on el.dataset.pvSrc so turning translation off restores it.
    const liveTx = new Map<string, string>();
    async function liveTranslate(src: string): Promise<string | null> {
      const target = readerRef.current.translateLang || "English";
      const key = target + "|" + src;
      const cached = liveTx.get(key);
      if (cached) return cached;
      try {
        const rr = await fetch(remoteUrl("/lib/translate"), {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: src, target_lang: target }),
        });
        if (!rr.ok) return null;
        const d = await rr.json();
        const t = ((d?.translated as string) || "").trim();
        if (t) { liveTx.set(key, t); return t; }
        return null;
      } catch { return null; }
    }
    function reflow() {
      try { if (curCfiRef.current && viewRef.current) viewRef.current.goTo(curCfiRef.current); } catch { /* */ }
    }
    function applyTranslation(index: number) {
      const blocks = sectionBlocks[index] || [];
      const misses: { el: HTMLElement; src: string }[] = [];
      for (const b of blocks) {
        const el = b.el as HTMLElement;
        if (!el) continue;
        if (el.dataset.pvSrc == null) el.dataset.pvSrc = el.textContent || "";
        const en = paraEnglish(bookId, b.text);
        if (en) { if (el.textContent !== en) el.textContent = en; }
        else misses.push({ el, src: b.text });
      }
      reflow();
      if (misses.length) {
        void (async () => {
          let changed = false;
          for (const m of misses) {
            if (!readerRef.current.translateOn) return;
            const t = await liveTranslate(m.src);
            if (t && readerRef.current.translateOn && m.el.isConnected) { m.el.textContent = t; changed = true; }
          }
          if (changed) reflow();
        })();
      }
    }
    function restoreTranslation(index: number) {
      const blocks = sectionBlocks[index] || [];
      for (const b of blocks) {
        const el = b.el as HTMLElement;
        if (el && el.dataset.pvSrc != null) el.textContent = el.dataset.pvSrc;
      }
      reflow();
    }
    txApplyRef.current = (on: boolean) => {
      if (on) applyTranslation(state.curLoadedIndex);
      else restoreTranslation(state.curLoadedIndex);
    };

    // --- TRANSLATED read-aloud: speak the translated page in the target
    // language via the browser's speech engine (multilingual + word-boundary
    // events for the overlay highlight). Original read-aloud (Kokoro) is
    // untouched; this branch runs only when reader.translateOn. ---
    let pickedVoice: SpeechSynthesisVoice | null = null;
    function pickVoice(code: string): SpeechSynthesisVoice | null {
      try {
        const vs = window.speechSynthesis?.getVoices?.() || [];
        const two = code.slice(0, 2).toLowerCase();
        return vs.find((v) => v.lang?.toLowerCase() === code.toLowerCase())
          || vs.find((v) => v.lang?.toLowerCase().startsWith(two)) || null;
      } catch { return null; }
    }
    function speakBrowser(text: string, code: string): Promise<void> {
      return new Promise((resolve) => {
        const synth = window.speechSynthesis;
        if (!synth || typeof SpeechSynthesisUtterance === "undefined") { resolve(); return; }
        try { synth.cancel(); } catch { /* */ }
        const u = new SpeechSynthesisUtterance(text);
        if (code) u.lang = code;
        const v = pickedVoice || (pickedVoice = pickVoice(code));
        if (v) u.voice = v;
        u.rate = rateRef.current || 1;
        u.onboundary = (e) => { if (typeof e.charIndex === "number" && !disposed) setTxIdx(e.charIndex); };
        const done = () => { if (!disposed) setTxIdx(null); resolve(); };
        u.onend = done; u.onerror = done;
        try { synth.speak(u); } catch { done(); }
      });
    }
    async function readLoopTranslated() {
      const myGen = ++state.gen;
      state.playing = true; state.paused = false; state.stopped = false; setUI("playing");
      pickedVoice = pickVoice(LANG_CODE[readerRef.current.translateLang] || "en-US");
      while (state.playing && state.gen === myGen) {
        if (state.paused) { await sleep(150); continue; }
        const page = txRef.current;
        const src = (page.text || "").trim();
        if (!src) { await sleep(200); continue; }
        log("translating…");
        let translated = "";
        try { translated = await fetchTranslation(bookId, page.cfi, src, readerRef.current.translateLang, undefined, page.paras); } catch { /* */ }
        if (!state.playing || state.gen !== myGen) break;
        if (translated) {
          log("reading (translated)…");
          await speakBrowser(translated, LANG_CODE[readerRef.current.translateLang] || "");
          if (!state.playing || state.gen !== myGen) break;
        } else { await sleep(400); }
        const beforeCfi = curCfiRef.current;
        log("turning page…");
        try { await view.next(); } catch { /* */ }
        const deadline = Date.now() + 2800;
        while (curCfiRef.current === beforeCfi && Date.now() < deadline && state.playing) await sleep(80);
        if (curCfiRef.current === beforeCfi) { log("end of book"); break; }
        await sleep(320);   // let the visible-page snapshot (txRef) update
      }
      if (state.gen === myGen) { state.playing = false; state.paused = false; setUI("idle"); }
    }

    (async () => {
     try {
      const meta = await getBook(bookId);
      if (meta?.title) setTitle(meta.title);
      const blob = await getBookBlob(bookId);
      if (!blob) { log("book not found"); setFatal("Book not found in your library."); return; }
      const base = import.meta.env.BASE_URL;
      log("loading engine…");
      await import(/* @vite-ignore */ base + "foliate/view.js");
      const cfiMod = await import(/* @vite-ignore */ base + "foliate/epubcfi.js");
      const compare = cfiMod.compare;
      if (disposed) return;

      view = document.createElement("foliate-view");
      // Must fill the host box or the paginator computes wrong column geometry
      // and page-turns don't advance (text renders but goTo can't flip). The
      // standalone reader sizes the element via CSS; do the same here.
      view.style.display = "block";
      view.style.width = "100%";
      view.style.height = "100%";
      hostRef.current?.appendChild(view);

      view.addEventListener("load", (e: CustomEvent) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { doc, index } = (e as any).detail;
        state.curLoadedIndex = index;
        const blocks: { cfi: string | null; text: string; el: Element }[] = [];
        doc.querySelectorAll("p, h1, h2, h3, h4, li, blockquote, dd").forEach((el: Element) => {
          const text = (el.textContent || "").replace(/\s+/g, " ").trim();
          if (text.length < 2) return;
          let cfi: string | null = null;
          try {
            const range = doc.createRange();
            range.selectNodeContents(el);
            cfi = view.getCFI(index, range);
          } catch { /* */ }
          blocks.push({ cfi, text, el });
        });
        sectionBlocks[index] = blocks;
        // If translation is on, swap this freshly-loaded section's paragraphs to
        // English before foliate paginates it.
        if (readerRef.current.translateOn) { try { applyTranslation(index); } catch { /* */ } }
        // Taps inside foliate's content iframe never bubble to the outer React
        // onClick, so wire the Kindle-style tap zones directly onto the section
        // document: left/right edge = page turn, top/bottom band or centre =
        // toggle chrome. Skip real links and active text selections.
        try {
          const win = doc.defaultView as Window;
          doc.addEventListener("click", (ev: MouseEvent) => {
            const t = ev.target as Element | null;
            if (t && t.closest && t.closest("a[href], button, input, textarea, select, [contenteditable]")) return;
            const sel = win.getSelection && win.getSelection();
            if (sel && !sel.isCollapsed) return;
            // foliate sizes the content iframe to the FULL width of the whole
            // section (all columns) and scrolls its parent to show one page, so
            // win.innerWidth / ev.clientX are section-space, not screen-space —
            // that made edge taps land on the wrong zone. Convert the tap to
            // top-viewport coordinates using the iframe's on-screen rect.
            let w = win.innerWidth || 1, h = win.innerHeight || 1, px = ev.clientX, py = ev.clientY;
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const fe = (win as any).frameElement as HTMLElement | null;
              const top = win.top as Window | null;
              if (fe && top) {
                const r = fe.getBoundingClientRect();
                px = ev.clientX + r.left;
                py = ev.clientY + r.top;
                w = top.innerWidth || w;
                h = top.innerHeight || h;
              }
            } catch { /* cross-origin — fall back to iframe-local coords */ }
            const x = px / w;
            const y = py / h;
            if (y < 0.16 || y > 0.84) { setChromeVisible((v) => !v); return; }
            if (x < 0.24) { ctl.current.prev(); return; }
            if (x > 0.76) { ctl.current.next(); return; }
            setChromeVisible((v) => !v);
          }, true);
        } catch { /* */ }
      });
      view.addEventListener("relocate", (e: CustomEvent) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = (e as any).detail;
        state.curCfi = d.cfi || state.curCfi;
        curCfiRef.current = d.cfi || curCfiRef.current;
        curHrefRef.current = d.tocItem?.href || d.section?.href || curHrefRef.current;
        try { if (d.cfi) localStorage.setItem(posKey, d.cfi); } catch { /* */ }
        if (!disposed) {
          setFrac(d.fraction || 0);
          if (d.tocItem?.label) setChapter(String(d.tocItem.label).trim());
          const hit = bmRef.current.find((b) => b.cfi === d.cfi);
          setBmId(hit ? hit.id : null);
          if (readerRef.current.translateOn)
            setTimeout(() => { if (!disposed) { try { setTranslatePage(getVisiblePageText()); } catch { /* */ } } }, 90);
        }
        if (!state.playing) log(`reading · ${Math.round((d.fraction || 0) * 100)}%`);
      });

      try {
        await view.open(blob);
        viewRef.current = view;
        // Preload the WHOLE book's translation: bind the pretranslation artifact
        // (classics by id, cloud books by sha256, or by title — QualityLand 2.0
        // binds by title) so the translate overlay serves each page from the
        // map instantly instead of live-translating a whole section (the "big
        // scroll"). Fire-and-forget; best effort.
        void (async () => {
          try {
            await loadPretranslationForBook(bookId, meta?.fileName, meta?.cloudSha256, {
              title: meta?.title, author: meta?.author,
            });
            // The first section may have loaded (and applied live translation)
            // before the map arrived — re-apply now so it uses the map's English.
            if (!disposed && readerRef.current.translateOn) { try { applyTranslation(state.curLoadedIndex); } catch { /* */ } }
          } catch { /* pretranslation is best-effort */ }
        })();
        const r = view.renderer;
        r.setAttribute("flow", "paginated");
        r.setAttribute("gap", "6%");
        r.setAttribute("margin", `${readerRef.current.margin || 26}px`);
        r.setAttribute("max-inline-size", "42rem");
        r.setAttribute("max-column-count", "1");
        try { if (r.setStyles) r.setStyles(readerCss(readerRef.current)); } catch { if (r.setStyles) r.setStyles(READ_CSS); }
        // Chapters (TOC) + saved bookmarks for the footer tiles.
        try { if (!disposed) setToc(view.book?.toc || []); } catch { /* */ }
        listBookmarks(bookId).then((list) => { if (!disposed) bmRef.current = list.map((b) => ({ id: b.id, cfi: b.cfi })); }).catch(() => {});
        // resume last position if we have one, else paint the first page
        let resumed = false;
        try {
          const saved = localStorage.getItem(posKey);
          if (saved) { await view.goTo(saved); resumed = true; }
        } catch { /* */ }
        if (!resumed) r.next();
        log("ready — tap Read");
      } catch (err) {
        log("open error: " + String(err).slice(0, 70));
        return;
      }

      ctl.current = {
        toggle: () => {
          // Translation is now applied IN PLACE (the page text is already the
          // target language), so read-aloud always uses the normal Kokoro loop —
          // it reads whatever text is on screen.
          if (!state.playing) { unlock(); readLoop(compare); }
          else if (!state.paused) {
            state.paused = true;
            try { audio.pause(); } catch { /* */ }
            setUI("paused"); log("paused");
          } else {
            state.paused = false;
            try { void audio.play(); } catch { /* */ }
            setUI("playing");
          }
        },
        prev: () => { stopRead(); try { view.prev(); } catch { try { view.goLeft(); } catch { /* */ } } },
        next: () => { stopRead(); try { view.next(); } catch { try { view.goRight(); } catch { /* */ } } },
        stop: () => stopRead(),
      };
     } catch (err) {
       // Surface any failure (e.g. the foliate engine not loading on an older
       // BOOX/NeoBrowser Chromium) instead of a silent blank screen.
       if (!disposed) { setFatal(String(err).slice(0, 180)); log("error"); }
     }
    })();

    return () => {
      disposed = true;
      state.playing = false; state.stopped = true; state.gen++;
      try { audio.pause(); audio.src = ""; } catch { /* */ }
      try { window.speechSynthesis?.cancel(); } catch { /* */ }
      try { if (view && view.remove) view.remove(); } catch { /* */ }
    };
  }, [bookId]);

  const showPause = playState === "playing";
  const playLabel = playState === "playing" ? "Pause" : playState === "paused" ? "Resume" : "Read aloud";
  const pct = Math.round(frac * 100);

  return (
    <div className="pv-fg" style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column",
      backgroundColor: "var(--pv-bg)", fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif" }}>
      {/* Classic top toolbar — title only; tap the page to show/hide it */}
      <ReaderToolbar title={title || "PageVoice"} chapter={chapter} hidden={!chromeVisible}
        playing={playState === "playing"} paused={playState === "paused"}
        onPlay={() => ctl.current.toggle()} onPause={() => ctl.current.toggle()} onResume={() => ctl.current.toggle()} />

      {/* Reading area — Amazon/Kindle-style zones: tap the TOP or BOTTOM band
          to reveal/hide the menus (the page area shrinks to make room), tap the
          LEFT/RIGHT edge to turn the page, tap the centre to toggle menus.
          Swipe still turns pages via foliate. */}
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}
        onClick={(e) => {
          const rc = (e.currentTarget as HTMLElement).getBoundingClientRect();
          const x = (e.clientX - rc.left) / rc.width;
          const y = (e.clientY - rc.top) / rc.height;
          if (y < 0.16 || y > 0.84) { setChromeVisible((v) => !v); return; }
          if (x < 0.24) { ctl.current.prev(); return; }
          if (x > 0.76) { ctl.current.next(); return; }
          setChromeVisible((v) => !v);
        }}>
        <div ref={hostRef} style={{ position: "absolute", inset: 0, backgroundColor: themeBg(reader.theme) }} />
        {fatal && (
          <div style={{ position: "absolute", inset: 0, background: "#ffffff", color: "#000000",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 18, padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>The reader couldn't load</div>
            <div style={{ fontSize: 15, maxWidth: 340, wordBreak: "break-word", opacity: 0.85 }}>{fatal}</div>
            <Link to={`/reader/${bookId}`} style={{ fontSize: 18, fontWeight: 800, background: "#000",
              color: "#fff", borderRadius: 10, padding: "14px 22px", textDecoration: "none" }}>Open in classic reader</Link>
            <Link to="/" style={{ color: "#000", fontSize: 17, fontWeight: 700 }}>‹ Library</Link>
          </div>
        )}
      </div>

      {/* Classic bottom bar — Home · Bookmark · Settings · Translate · Play.
          Hidden by default (immersive); revealed by tapping top/bottom. */}
      {chromeVisible && (
      <div ref={barRef} className="relative z-30 flex flex-col gap-1 px-3 pt-2"
        style={{ backgroundColor: "var(--pv-bg)", paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 8px)" }}>
        <div className="pv-progress-track h-1 w-full overflow-hidden rounded">
          <div className="pv-progress h-full" style={{ width: `${pct}%` }} />
        </div>
        <div className="pv-fg-soft flex items-center justify-between text-xs leading-tight">
          <span className="truncate">{chapter || status}</span>
          <span className="tabular-nums">{pct}%</span>
        </div>
        <div className="flex items-stretch gap-1.5 pt-0.5">
          <Link to="/" aria-label="Home" className="pv-fg flex items-center justify-center rounded-lg py-3 active:opacity-60" style={{ flex: 1 }}>
            <HomeGlyph />
          </Link>
          <button aria-label="Bookmark" onClick={() => void toggleBookmark()}
            className="flex items-center justify-center rounded-lg py-3 active:opacity-60"
            style={{ flex: 1, color: bmId ? "var(--pv-accent)" : "var(--pv-fg)" }}>
            <BookmarkGlyph filled={!!bmId} />
          </button>
          <button aria-label="Settings" onClick={() => setSettingsOpen(true)}
            className="pv-fg flex items-center justify-center rounded-lg py-3 active:opacity-60" style={{ flex: 1 }}>
            <GearGlyph />
          </button>
          <button aria-label="Translate" onClick={() => setReader({ translateOn: !reader.translateOn })}
            className="flex items-center justify-center rounded-lg py-3 active:opacity-60"
            style={{ flex: 1, color: reader.translateOn ? "var(--pv-accent)" : "var(--pv-fg)" }}>
            <GlobeGlyph />
          </button>
          <button aria-label={playLabel} onClick={() => ctl.current.toggle()}
            className="flex items-center justify-center rounded-lg py-3 active:opacity-80"
            style={{ flex: 1.4, backgroundColor: "var(--pv-accent)", color: "#fff" }}>
            <PlayPauseGlyph showPause={showPause} size={22} />
          </button>
        </div>
      </div>
      )}

      {settingsOpen && (
        <SettingsPanel onClose={() => setSettingsOpen(false)}
          onOpenChapters={() => { setSettingsOpen(false); setTocOpen(true); }} />
      )}
      {tocOpen && (
        <TOCPanel items={toc} onClose={() => setTocOpen(false)}
          onSelect={(href) => { setTocOpen(false); try { viewRef.current?.goTo(href); } catch { /* */ } }} />
      )}

      {/* Translation is applied in place (paragraph text swapped to the target
          language), so the book paginates the translation itself — no overlay. */}
    </div>
  );
}

function HomeGlyph() {
  return (
    <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
      <path d="M10 20v-5h4v5" />
    </svg>
  );
}
function BookmarkGlyph({ filled }: { filled: boolean }) {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"}
      stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z" />
    </svg>
  );
}
function GearGlyph() {
  return (
    <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="21" y1="6" x2="13" y2="6" /><line x1="9" y1="6" x2="3" y2="6" />
      <line x1="21" y1="12" x2="15" y2="12" /><line x1="11" y1="12" x2="3" y2="12" />
      <line x1="21" y1="18" x2="17" y2="18" /><line x1="13" y1="18" x2="3" y2="18" />
      <circle cx="11" cy="6" r="2" /><circle cx="13" cy="12" r="2" /><circle cx="15" cy="18" r="2" />
    </svg>
  );
}
function GlobeGlyph() {
  return (
    <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 3 4 6 4 9s-1.5 6-4 9c-2.5-3-4-6-4-9s1.5-6 4-9z" />
    </svg>
  );
}

export default FoliateReaderView;
